import type { ReactNode } from "react";
import { ArrowLeft, ArrowRight, ChevronFirst, ChevronLast } from "lucide-react";

/** 一度に見せるページ番号の数（ngtg_msc の CustomPageController.windowSize）。 */
const WINDOW_SIZE = 5;

/**
 * 一覧下端のページャ（ページ送り＋全件数）。
 *
 * ngtg_msc の `CustomPageController` に合わせ、最初/前 → 現在地周辺のページ番号
 * （最大 5 個の窓）→ 次/最後 の並びにする。HeroUI の Pagination を使わないのは、
 * 最初/最後へのジャンプが無く、選択中チップの見た目（枠線つき角丸）も
 * 揃えられないため。
 *
 * ページングはサーバー側で行うため、総ページ数は総件数とページサイズから求める。
 */
export default function Paginator({
  total,
  pageSize,
  page,
  onChange,
}: {
  total: number;
  pageSize: number;
  page: number;
  onChange: (page: number) => void;
}) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(page, 1), lastPage);

  // current を中心に最大 WINDOW_SIZE 個。端に寄ったら反対側へ広げる。
  let start = Math.max(1, current - Math.floor(WINDOW_SIZE / 2));
  const end = Math.min(lastPage, start + WINDOW_SIZE - 1);
  start = Math.max(1, end - WINDOW_SIZE + 1);
  const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);

  return (
    <div className="flex items-center justify-end gap-4 px-6 py-2.5">
      <div className="flex items-center">
        <IconButton
          label="最初のページ"
          isDisabled={current <= 1}
          onClick={() => onChange(1)}
        >
          <ChevronFirst size={20} />
        </IconButton>
        <IconButton
          label="前のページ"
          isDisabled={current <= 1}
          onClick={() => onChange(current - 1)}
        >
          <ArrowLeft size={20} />
        </IconButton>

        {pages.map((p) => (
          <button
            key={p}
            type="button"
            aria-label={`${p} ページ目`}
            aria-current={p === current ? "page" : undefined}
            onClick={() => onChange(p)}
            className={[
              "m-[3px] min-w-9 cursor-pointer rounded border px-2.5 py-1 text-sm transition-colors",
              p === current
                ? "border-brand-500 bg-brand-500 text-white"
                : "border-default-300 bg-white hover:bg-default-100",
            ].join(" ")}
          >
            {p}
          </button>
        ))}

        <IconButton
          label="次のページ"
          isDisabled={current >= lastPage}
          onClick={() => onChange(current + 1)}
        >
          <ArrowRight size={20} />
        </IconButton>
        <IconButton
          label="最後のページ"
          isDisabled={current >= lastPage}
          onClick={() => onChange(lastPage)}
        >
          <ChevronLast size={20} />
        </IconButton>
      </div>

      <span className="text-xs">全 {total} 件</span>
    </div>
  );
}

function IconButton({
  label,
  isDisabled,
  onClick,
  children,
}: {
  label: string;
  isDisabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={isDisabled}
      onClick={onClick}
      className="cursor-pointer rounded-full p-2 text-default-600 transition-colors hover:bg-default-100 disabled:cursor-default disabled:text-default-300 disabled:hover:bg-transparent"
    >
      {children}
    </button>
  );
}
